import React, { FC } from 'react';
import { TypeMediaTextTitle, TypeProduct } from '@/core/generated-types';
import { CarouselProducts } from '../Carousel';
import { DynamicComponent, HeaderText, LuvImage } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { usePathname } from '@/navigation';
import { UniversalCta } from '../UniversalCta';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';

interface PlayKitInterface {
  product: TypeProduct;
  carousel: TypeMediaTextTitle[];
}

interface PlayKitBodyInterface {
  longDescription?: string;
  productValueProps?: TypeMediaTextTitle[];
}

export const PlayKitDetailsBody: FC<PlayKitBodyInterface> = ({
  longDescription,
  productValueProps,
}) => {
  return (
    <div className="col-span-12">
      <>
        <p className="mb-4 leading-6">{longDescription}</p>
        <ul className="mb-4 flex flex-col gap-4">
          {productValueProps?.map((item) => {
            const {
              sys: { id },
              fields: { textHeading, image },
            } = item;

            return (
              <li
                key={id}
                className="flex-start flex flex-row items-center gap-3"
              >
                <LuvImage
                  imageData={image}
                  height={50}
                  width={50}
                  altText={textHeading}
                  className="mx-0 w-[30px] flex-shrink-0"
                />
                <p className="w-auto">{textHeading}</p>
              </li>
            );
          })}
        </ul>
      </>
    </div>
  );
};

export const PlayKitDetailsHeader: FC<PlayKitInterface> = ({
  product,
  carousel,
}) => {
  const {
    sys: { id },
  } = product;
  const {
    badge,
    title,
    longDescription,
    productValueProps,
    featuredImage,
    shipMessage,
    cta,
    ageDescription,
  } = product.fields;
  const {
    api: { updateInitialProduct },
  } = useSubFlowV3Data();

  const pathname = usePathname();

  const handleClick = () => {
    updateInitialProduct(product);
  };

  return (
    <div className="mx-auto grid grid-cols-1 pb-4 md:bg-tan md:px-6 lg:grid-cols-12 lg:gap-2">
      <div className="lg:col-span-7 lg:hidden lg:p-12">
        <CarouselProducts
          id={id}
          components={[featuredImage, ...carousel] as TypeMediaTextTitle[]}
          key={pathname}
        />
      </div>
      <div className="hidden lg:col-span-7 lg:block lg:p-12">
        <LuvImage imageData={featuredImage} height={800} width={800} />
      </div>
      <div className="col-span-full px-6 md:px-0 lg:col-span-5 lg:col-start-8 lg:pr-8">
        <div className="flex h-full flex-col justify-evenly">
          <div className="flex hidden lg:block">
            {badge && <DynamicComponent component={badge} />}
            <HeaderText text={title} size={HeaderSize.H1} />
            <p className="mb-2 text-base font-bold">{ageDescription}</p>
            <PlayKitDetailsBody
              longDescription={longDescription}
              productValueProps={productValueProps as TypeMediaTextTitle[]}
            />
            <div className="mx-auto mt-4 lg:mx-0 lg:mt-4">
              <div className="col-span-12">
                <p className="pb-2 text-base md:text-lg">{shipMessage}</p>
              </div>
              <div className="col-span-12">
                {cta && (
                  <UniversalCta
                    link={cta}
                    className="max-w-xs"
                    onClick={handleClick}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 w-full lg:mx-0 lg:mt-0 lg:hidden">
            <div className="col-span-12">
              <p className="pb-2 text-center text-base md:text-lg">
                {shipMessage}
              </p>
            </div>
            {cta && <UniversalCta link={cta} />}
          </div>
        </div>
      </div>
    </div>
  );
};
